import {Component, Input, OnInit} from '@angular/core';
import {
    Aangifte,
    UitlegResponse,
    UitlegVelden
} from "@snelstart-ventures/fiscaal/domain-logic";
import {BokkzIbDataService  } from "@snelstart-ventures/fiscaal/domain-logic";
import {AbstractControl, FormBuilder, FormControl, FormGroup} from "@angular/forms";
import {BehaviorSubject} from "rxjs";
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { CommonModule } from '@angular/common';

interface PropertyConfig {
    positiveLabel: string;
    negativeLabel: string;
}

@Component({
  selector: 'snelstart-venture-fiscaal-feature-uitleg-aanslag',
  templateUrl: './uitleg-aanslag.component.html',
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    CommonModule
  ],
  styleUrls: ['./uitleg-aanslag.component.scss']
})
export class UitlegAanslagComponent implements OnInit {
    @Input() public id!: string;
    public aangifte = <Aangifte>{};

    @Input() partner = new BehaviorSubject<boolean>(false);
    private currentAangifte = <Aangifte>{};
    private partnerAangifte = <Aangifte>{};

    uitlegFormGroup: FormGroup;
    uitleg: UitlegResponse = {aangever: {}, partner: {}};

    constructor(  public dataService: BokkzIbDataService, private fb: FormBuilder) {
        this.uitlegFormGroup = this.fb.group<UitlegResponse>(<UitlegResponse>{
            aangever: this.fb.group<UitlegVelden>({
                nogVerschuldigdOverBox1Looninkomen: null as unknown as number,
                nogVerschuldigdOverBox1Winst: null as unknown as number,
                effectOuderenkortingBox1Inkomen: null as unknown as number,
                effectAftrekpostenInclAanslaggrens: null as unknown as number,
                effectPositiefInkomenEigenWoning: null as unknown as number,
                effectOuderenkortingAftrekposten: null as unknown as number,
                nogTeBetalenOverBox2: null as unknown as number,
                effectOuderenkortingBox2: null as unknown as number,
                nogTeBetalenOverBox3: null as unknown as number,
                effectOuderenkortingBox3: null as unknown as number,
                alOpgelegdeVoorlopigeAanslag: null as unknown as number,
                dividendbelasting: null as unknown as number,
                effectRuimteLoonheffingskorting: null as unknown as number,
                kortinggroenebeleggingen: null as unknown as number,
                inkomensafhankelijkecombinatiekorting: null as unknown as number,
                verhogingVanwegePartner: null as unknown as number,
                onbenutteEigenHeffingskorting: null as unknown as number,
                maximeringHeffingskorting: null as unknown as number,
                aanslaggrens: null as unknown as number,
                verzamelpost: null as unknown as number,
                bedragAanslag: null as unknown as number,
                isVerklaard: null as unknown as boolean,
                verschil: null as unknown as number,
            }),
            partner: this.fb.group<UitlegVelden>({
                nogVerschuldigdOverBox1Looninkomen: null as unknown as number,
                nogVerschuldigdOverBox1Winst: null as unknown as number,
                effectOuderenkortingBox1Inkomen: null as unknown as number,
                effectAftrekpostenInclAanslaggrens: null as unknown as number,
                effectPositiefInkomenEigenWoning: null as unknown as number,
                effectOuderenkortingAftrekposten: null as unknown as number,
                nogTeBetalenOverBox2: null as unknown as number,
                effectOuderenkortingBox2: null as unknown as number,
                nogTeBetalenOverBox3: null as unknown as number,
                effectOuderenkortingBox3: null as unknown as number,
                alOpgelegdeVoorlopigeAanslag: null as unknown as number,
                dividendbelasting: null as unknown as number,
                effectRuimteLoonheffingskorting: null as unknown as number,
                kortinggroenebeleggingen: null as unknown as number,
                inkomensafhankelijkecombinatiekorting: null as unknown as number,
                verhogingVanwegePartner: null as unknown as number,
                onbenutteEigenHeffingskorting: null as unknown as number,
                maximeringHeffingskorting: null as unknown as number,
                aanslaggrens: null as unknown as number,
                verzamelpost: null as unknown as number,
                bedragAanslag: null as unknown as number,
                isVerklaard: null as unknown as boolean,
                verschil: null as unknown as number,
            }),
        });
    }

    propertyNamesToCheck: string[] = [
        'nogVerschuldigdOverBox1Looninkomen',
        'nogVerschuldigdOverBox1Winst',
        'effectOuderenkortingBox1Inkomen',
        'effectAftrekpostenInclAanslaggrens',
        'effectPositiefInkomenEigenWoning',
        'effectOuderenkortingAftrekposten',
        'nogTeBetalenOverBox2',
        'effectOuderenkortingBox2',
        'nogTeBetalenOverBox3',
        'effectOuderenkortingBox3',
        'alOpgelegdeVoorlopigeAanslag',
        'dividendbelasting',
        'effectRuimteLoonheffingskorting',
        'kortinggroenebeleggingen',
        'inkomensafhankelijkecombinatiekorting',
        'verhogingVanwegePartner',
        'onbenutteEigenHeffingskorting',
        'maximeringHeffingskorting',
        'aanslaggrens',
        'verzamelpost',
        'bedragAanslag',
        'isVerklaard',
        'verschil',
    ];

    propertyTextMap: Record<string, PropertyConfig> = {
        nogVerschuldigdOverBox1Looninkomen: {
            positiveLabel: 'Er is over het inkomen box 1 extra belasting verschuldigd:',
            negativeLabel: 'Er is over het inkomen box 1 teveel loonheffing ingehouden:',
        },
        nogVerschuldigdOverBox1Winst: {
            positiveLabel: 'Over de belaste winst uit onderneming(en) is de belasting:',
            negativeLabel: 'Over het belastste verlies uit onderneming(en) is de belasting:',
        },
        effectOuderenkortingBox1Inkomen: {
            positiveLabel: 'Het inkomen van de partner heeft een effect op de heffingskorting voor ouderen:',
            negativeLabel: 'Het inkomen van de partner heeft een effect op de heffingskorting voor ouderen:',
        },
        effectAftrekpostenInclAanslaggrens: {
            positiveLabel: '-',
            negativeLabel: 'De belastingvermindering door aftrekposten is:',
        },
        effectPositiefInkomenEigenWoning: {
            positiveLabel: 'Er is een bijtelling vanwege de eigen woning; hierover is belasting verschuldigd:',
            negativeLabel: '-',
        },
        effectOuderenkortingAftrekposten: {
            positiveLabel: 'Door aftrekposten en belast vermogen is het effect op de heffingskorting voor ouderen:',
            negativeLabel: 'Door aftrekposten en belast vermogen is het effect op de heffingskorting voor ouderen:',
        },
        nogTeBetalenOverBox2: {
            positiveLabel: 'De inkomsten in box 2 (aanmerkelijk belang) zijn belast met:',
            negativeLabel: 'De negatieve inkomsten box 2 leiden tot minder belasting:',
        },
        effectOuderenkortingBox2: {
            positiveLabel: '-',
            negativeLabel: '-',
        },
        nogTeBetalenOverBox3: {
            positiveLabel: 'De belasting over het vermogen per 1 januari (box 3) is:',
            negativeLabel: '-',
        },
        effectOuderenkortingBox3: {
            positiveLabel: 'Het inkomen heeft een effect op de heffingskorting voor ouderen:',
            negativeLabel: 'Het inkomen heeft een effect op de heffingskorting voor ouderen:',
        },
        alOpgelegdeVoorlopigeAanslag: {
            positiveLabel: 'De al opgelegde voorlopige teruggave bedraagt:',
            negativeLabel: 'De al opgelegde voorlopige aanslag bedraagt:',
        },
        dividendbelasting: {
            positiveLabel: 'Er is aan ingehouden dividendbelasting te verrekenen:',
            negativeLabel: 'Er is aan ingehouden dividendbelasting te verrekenen:',
        },
        effectRuimteLoonheffingskorting: {
            positiveLabel: 'Vanwege gering (arbeids)inkomen is er recht op extra (loon)heffingskorting:',
            negativeLabel: 'Vanwege gering (arbeids)inkomen is er recht op extra (loon)heffingskorting:',
        },
        kortinggroenebeleggingen: {
            positiveLabel: 'Vanwege groene beleggingen is er een extra heffingskorting:',
            negativeLabel: 'Vanwege groene beleggingen is er een extra heffingskorting:',
        },
        inkomensafhankelijkecombinatiekorting: {
            positiveLabel: 'Er is recht op de zgn. inkomensafhankelijke combinatiekorting:',
            negativeLabel: 'Er is recht op de zgn. inkomensafhankelijke combinatiekorting:',
        },
        verhogingVanwegePartner: {
            positiveLabel: 'De heffingskortingen worden vanwege fiscaal partnerschap verhoogd met:',
            negativeLabel: 'De heffingskortingen worden vanwege fiscaal partnerschap verhoogd met:',
        },
        onbenutteEigenHeffingskorting: {
            positiveLabel: 'Vanwege gering inkomen wordt nu extra heffingskorting benut:',
            negativeLabel: 'Vanwege gering inkomen wordt nu extra heffingskorting benut:',
        },
        maximeringHeffingskorting: {
            positiveLabel: 'Er is een maximum aan het recht op heffingskortingen; effect hiervan is:',
            negativeLabel: 'Er is een maximum aan het recht op heffingskortingen; effect hiervan is:',
        },
        aanslaggrens: {
            positiveLabel: 'De regeling over de zgn. aanslaggrens betekent een voordeel van:',
            negativeLabel: 'De regeling over de zgn. aanslaggrens betekent een voordeel van:',
        },
        verzamelpost: {
            positiveLabel: 'Er zijn diverse niet nader benoemde effecten (verzamelpost):',
            negativeLabel: 'Er zijn diverse niet nader benoemde effecten (verzamelpost):',
        },
        bedragAanslag: {
            positiveLabel: 'Bovenstaande geeft een aanslag van:',
            negativeLabel: 'Bovenstaande geeft een aanslag van:',
        },
        isVerklaard: {
            positiveLabel: '1',
            negativeLabel: '2',
        },
        verschil: {
            positiveLabel: 'Verschil in uitleg',
            negativeLabel: 'Verschil in uitleg',
        },
    };


    getPropertyValue(propertyName: string): number | null {
        const control = this.uitlegFormGroup.get(['aangever', propertyName]);
        return control ? control.value : null;
    }

    getLabelText(propertyName: string): string {
        const value = this.getPropertyValue(propertyName);
        const config = this.propertyTextMap[propertyName];
        if (value !== null && value > 0) {
            return config.positiveLabel;
        } else {
            return config.negativeLabel;
        }
    }

    public ngOnInit(): void {
        // setTimeout(() => {
        //     this.dataService.getUitleg();
        // }, 1000);
        this.dataService.uitlegResponse.subscribe((value) => {
            this.uitleg = value;
            this.uitlegFormGroup.patchValue(this.uitleg)
        });
    }

    disableFormGroup(formGroup: FormGroup | AbstractControl): void {
        if (formGroup instanceof FormGroup) {
            Object.keys(formGroup.controls).forEach((key) => {
                const control = formGroup.get(key);
                if (control instanceof FormControl) {
                    control.disable();
                } else if (control instanceof FormGroup) {
                    this.disableFormGroup(control);
                }
            });
        }
    }

}
